import { MOMENT_TYPES } from '../hostStateSlice';
import {
  GAMESTATE_ROUND,
  GAMESTATE_ROUNDSTATE_ROUND_INTRO,
  GAMESTATE_LOBBY,
  GAMESTATE_INSTRUCTIONS,
  GAMESTATE_ROUND_PRACTICE,
  GAMESTATE_ROUND_1,
  GAMESTATE_ROUND_FINAL,
} from '../../gamestate/constants';
import { determineNextRound } from '../../gamestate/roundCompletionUtils';
import { registerCacheReset } from '../momentCacheManager';
import _ from 'lodash';
import logging from 'utils/logging';

const log = logging.getLogger('moments.RoundIntro');
log.setLevel('info');

// Track which rounds have had their intro triggered
const triggeredRounds = new Set();
registerCacheReset('round', () => triggeredRounds.clear());

/**
 * Compute who will be in control for a given round.
 * Mirrors the logic in GameState.getNextState().
 */
const computePlayerInControlName = (targetRound, players) => {
  if (!players || players.length === 0) return 'Player';

  if (targetRound === GAMESTATE_ROUND_PRACTICE || targetRound === GAMESTATE_ROUND_1) {
    return players[0]?.name || 'Player';
  }
  // Round 2+ — lowest scoring player
  return _.head(_.sortBy(players, ['score']))?.name || 'Player';
};

const buildTriggerResult = (targetRound, playerInControlName, playerCount) => {
  const narrationKey = `ROUND_INTRO_${targetRound}`;
  log.info(`Round intro triggered for ${targetRound}, player: ${playerInControlName}`);

  return {
    shouldTriggerAction: false,
    shouldGenerateNarration: true,
    narrationParams: {
      narrationKey,
      momentType: MOMENT_TYPES.ROUND_INTRO,
      context: {
        round: targetRound,
        playerInControlName,
        playerCount,
      }
    }
  };
};

export const RoundIntroMoment = {
  id: 'ROUND_INTRO',

  shouldTrigger: ({ prevState, currentState, currentPlayers, gameObject }) => {
    let targetRound = null;

    // Pre-generate during INSTRUCTIONS — next round is always practice
    if (currentState?.currentState === GAMESTATE_INSTRUCTIONS) {
      targetRound = GAMESTATE_ROUND_PRACTICE;
    }
    // Pre-generate during inter-round LOBBY (round is set = coming from a completed round)
    else if (currentState?.currentState === GAMESTATE_LOBBY && currentState?.round) {
      targetRound = determineNextRound(currentState.round, gameObject?.categories);
      if (!targetRound || targetRound === GAMESTATE_ROUND_FINAL) {
        return false;
      }
    }
    // Fallback: trigger at round.intro if pre-generation didn't happen
    else if (
      currentState?.currentState === GAMESTATE_ROUND &&
      currentState?.roundState === GAMESTATE_ROUNDSTATE_ROUND_INTRO
    ) {
      targetRound = currentState?.round;
    }

    if (!targetRound) return false;

    if (triggeredRounds.has(targetRound)) {
      log.debug(`Round intro already triggered for ${targetRound}`);
      return false;
    }

    triggeredRounds.add(targetRound);

    const playerInControlName = currentState?.playerInControl
      ? (currentPlayers?.find(p => p.id === currentState.playerInControl)?.name || 'Player')
      : computePlayerInControlName(targetRound, currentPlayers);

    return buildTriggerResult(targetRound, playerInControlName, currentPlayers?.length || 0);
  }
};

export const RoundIntroPlaybackMoment = {
  id: 'ROUND_INTRO_PLAYBACK',
  momentType: MOMENT_TYPES.ROUND_INTRO,

  // RoundIntroScreen handles all playback directly
  shouldPlay: () => false,
};

const api = {
  trigger: RoundIntroMoment,
  playback: RoundIntroPlaybackMoment
};

export default api;
